<template>
  <form class="stock-transfer-form" @submit.prevent="submitForm">
    <div v-if="localErrorMessage" class="alert alert-danger py-2">
      {{ localErrorMessage }}
    </div>

    <div class="row g-3 mb-3">
      <div class="col-md-6">
        <label class="form-label small fw-semibold">Site destination</label>
        <select
          v-model="form.destination_shop_id"
          class="form-select"
          :disabled="submitting"
        >
          <option :value="null">Sélectionner un site</option>
          <option v-for="shop in destinationShops" :key="shop.id" :value="shop.id">
            {{ shop.name }} <span v-if="shop.code">({{ shop.code }})</span>
          </option>
        </select>
      </div>

      <div class="col-md-6">
        <label class="form-label small fw-semibold">Notes</label>
        <input
          v-model="form.notes"
          type="text"
          class="form-control"
          :disabled="submitting"
        />
      </div>
    </div>

    <div class="stock-transfer-lines-toolbar d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
      <div>
        <h6 class="mb-1">Produits à transférer</h6>
        <div class="small text-muted">
          {{ form.items.length }} ligne(s)
        </div>
      </div>

      <div>
        <button
          type="button"
          class="btn btn-outline-primary btn-sm btn-icon-label"
          data-test="find-products-button"
          :disabled="submitting"
          @click="openProductSearchDialog"
        >
          <i class="fa fa-search"></i>
          <span class="btn-label ms-1">Trouver les produits</span>
        </button>
      </div>
    </div>

    <div class="table-responsive mb-3">
      <table class="table table-sm align-middle mb-0">
        <thead class="table-light">
          <tr>
            <th>Produit</th>
            <th style="width: 160px;">Référence</th>
            <th style="width: 120px;" class="text-end">Stock</th>
            <th style="width: 160px;" class="text-end">Quantité</th>
            <th style="width: 60px;"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!form.items.length">
            <td colspan="5" class="text-center text-muted py-3">
              Aucun produit sélectionné.
            </td>
          </tr>
          <tr v-for="(item, index) in form.items" :key="item.product_id || index">
            <td>
              <div class="fw-semibold">{{ item.product_name }}</div>
            </td>
            <td>{{ item.product_sku || '-' }}</td>
            <td class="text-end">{{ item.stock_quantity ?? '-' }}</td>
            <td>
              <PrimevueInputNumber
                v-model="item.quantity"
                class="w-100"
                :min="1"
                :maxFractionDigits="0"
                :disabled="submitting"
              />
            </td>
            <td class="text-end">
              <button
                type="button"
                class="btn btn-outline-danger btn-sm btn-action-icon"
                :disabled="submitting"
                title="Retirer"
                @click="removeItem(index)"
              >
                <i class="fa fa-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <PrimevueDialog
      v-model:visible="productSearchDialogVisible"
      :modal="true"
      :style="{ width: '50vw', maxWidth: '95vw' }"
      :breakpoints="{ '768px': '95vw' }"
    >
      <template #header>
        <h5 class="mb-0">Trouver les produits</h5>
      </template>

      <ProductSearchPicker
        :selected-product-ids="form.items.map((item) => item.product_id)"
        :show-stock="true"
        :show-price="false"
        :show-stock-filters="true"
        :disabled="submitting"
        @add="handleTransferProductAdd"
      />
    </PrimevueDialog>

    <div class="d-flex justify-content-end gap-2">
      <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="submitting" @click="$emit('cancel')">
        <i class="fa fa-xmark me-1" aria-hidden="true"></i>
        Annuler
      </button>
      <AppLoadingButton
        type="submit"
        class="btn btn-primary btn-sm btn-icon-label"
        :loading="submitting"
        loading-label="Enregistrement..."
      >
        <i class="fa fa-save"></i>
        <span class="btn-label ms-1">{{ mode === 'edit' ? 'Enregistrer les modifications' : 'Enregistrer' }}</span>
      </AppLoadingButton>
    </div>
  </form>
</template>

<script>
import ProductSearchPicker from '@/components/ProductSearchPicker.vue';

export default {
  name: 'StockTransferForm',
  components: {
    ProductSearchPicker,
  },
  props: {
    mode: {
      type: String,
      default: 'create',
    },
    transfer: {
      type: Object,
      default: null,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['save', 'cancel'],
  data() {
    return {
      shops: [],
      localErrorMessage: '',
      productSearchDialogVisible: false,
      form: {
        destination_shop_id: null,
        notes: '',
        items: [],
      },
    };
  },
  computed: {
    currentShopId() {
      return this.$store.getters['shop/currentShopId'];
    },
    destinationShops() {
      return this.shops.filter((shop) => Number(shop.id) !== Number(this.currentShopId));
    },
  },
  watch: {
    transfer: {
      immediate: true,
      handler() {
        this.resetForm();
      },
    },
  },
  mounted() {
    this.fetchShops();
  },
  methods: {
    async fetchShops() {
      try {
        const res = await this.$api.get('/shops', { params: { limit: 500 } });
        this.shops = res.data.data || [];
      } catch (error) {
        this.localErrorMessage = 'Impossible de charger les sites.';
      }
    },
    resetForm() {
      if (!this.transfer) {
        this.form = {
          destination_shop_id: null,
          notes: '',
          items: [],
        };
        return;
      }

      this.form = {
        destination_shop_id: this.transfer.destination_shop_id ?? this.transfer.destination_shop?.id ?? null,
        notes: this.transfer.notes || '',
        items: (this.transfer.items || []).map((item) => ({
          product_id: item.source_product_id || item.sourceProduct?.id || item.source_product?.id,
          product_name: item.product_name,
          product_sku: item.product_sku,
          stock_quantity: item.source_product?.stock_quantity ?? item.sourceProduct?.stock_quantity ?? null,
          quantity: Number(item.quantity || 1),
        })),
      };
    },
    removeItem(index) {
      this.form.items.splice(index, 1);
    },
    openProductSearchDialog() {
      this.productSearchDialogVisible = true;
    },
    handleTransferProductAdd({ product, quantity }) {
      if (!product) return;

      const quantityToAdd = Number(quantity || 1);
      const existingLine = this.form.items.find((item) => Number(item.product_id) === Number(product.id));

      if (existingLine) {
        return;
      }

      this.form.items.push({
        product_id: product.id,
        product_name: product.name,
        product_sku: product.sku,
        stock_quantity: product.stock_quantity ?? null,
        quantity: quantityToAdd,
      });
    },
    submitForm() {
      if (this.submitting) return;

      this.localErrorMessage = '';

      if (!this.form.destination_shop_id) {
        this.localErrorMessage = 'Sélectionnez un site destination.';
        return;
      }

      const items = this.form.items
        .filter((item) => item.product_id && Number(item.quantity || 0) > 0)
        .map((item) => ({
          product_id: item.product_id,
          quantity: Number(item.quantity),
        }));

      if (!items.length) {
        this.localErrorMessage = 'Ajoutez au moins un produit au transfert.';
        return;
      }

      this.$emit('save', {
        destination_shop_id: this.form.destination_shop_id,
        notes: this.form.notes || null,
        items,
      });
    },
  },
};
</script>
